import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PiStudent, PiChalkboardTeacher, PiGearSix } from 'react-icons/pi';
import logo from '../assets/logo.svg';
import AppStatic, { isDemoBuild } from '../config/AppStatic';
import Button from './ui/Button';
import Card from './ui/Card';

/**
 * Landing page for the Admin version when users are not authenticated.
 * Shows Pi-Schedule logo, welcome message, and user type selection options.
 */
function AdminLandingPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const demo = isDemoBuild();

    const pathParts = location.pathname.split('/').filter(Boolean);
    const reservedRoutes = new Set(['pi-admin', 'setup', 'schedule', 'onboarding', 'debug', 'documentation', 'invite', 'ultra-admin', 'institutions', 'unsubscribe']);
    const institutionSlug = pathParts.length === 1 && pathParts[0] && !reservedRoutes.has(pathParts[0].toLowerCase())
        ? pathParts[0]
        : null;

    const heroSubtitle = 'Your school scheduling platform';

    const pickDemoRole = (roleKey) => {
        AppStatic.setBuildRole(roleKey);
        navigate('/schedule', { replace: true });
    };

    const handleStudentClick = () => {
        const base = 'https://students.pi-schedule.com';
        window.location.href = institutionSlug ? `${base}/${institutionSlug}` : base;
    };

    const handleTeacherClick = () => {
        const base = 'https://teachers.pi-schedule.com';
        window.location.href = institutionSlug ? `${base}/${institutionSlug}` : base;
    };

    const handleAdminLogin = () => {
        window.location.href = 'https://admin.pi-schedule.com/pi-admin';
    };

    if (demo) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                        <div className="p-10">
                            <div className="mb-8">
                                <div className="flex items-center justify-center mb-6">
                                    <div className="relative flex items-center">
                                        <img
                                            src={logo}
                                            alt="Pi-Schedule Logo"
                                            className="w-20 h-20 drop-shadow-sm"
                                        />
                                        <div className="absolute inset-0 w-20 h-20 bg-blue-100/30 rounded-full blur-xl"></div>
                                        <h1 className="text-4xl font-light text-slate-800 tracking-tight ml-[-8px] relative z-10">
                                            Schedule
                                        </h1>
                                    </div>
                                </div>
                                <h2 className="text-xl font-semibold text-slate-800 text-center mb-3">
                                    Welcome to the Pi-Schedule demo
                                </h2>
                                <p className="text-slate-600 text-center leading-relaxed">
                                    Explore the app as an administrator, teacher, or student using sample data.
                                    Switch between these views anytime using the demo bar at the top of the screen.
                                </p>
                            </div>

                            <p className="text-sm font-medium text-slate-700 text-center mb-4">
                                Pick a role to get started
                            </p>
                            <div className="space-y-4">
                                <Button
                                    type="button"
                                    onClick={() => pickDemoRole('ADMIN')}
                                    className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white py-4 px-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                                >
                                    <div className="flex items-center justify-center space-x-3">
                                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                            <PiGearSix className="w-5 h-5" />
                                        </div>
                                        <span>Admin</span>
                                    </div>
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => pickDemoRole('TEACHERS')}
                                    className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white py-4 px-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                                >
                                    <div className="flex items-center justify-center space-x-3">
                                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                            <PiChalkboardTeacher className="w-5 h-5" />
                                        </div>
                                        <span>Teacher</span>
                                    </div>
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => pickDemoRole('STUDENTS')}
                                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                                >
                                    <div className="flex items-center justify-center space-x-3">
                                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                            <PiStudent className="w-5 h-5" />
                                        </div>
                                        <span>Student</span>
                                    </div>
                                </Button>
                            </div>
                        </div>
                    </Card>
                    <div className="mt-8 text-center">
                        <p className="text-sm text-slate-500 font-light">
                            © 2025 Pi-Schedule. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                {/* Main Card */}
                <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-10">
                        {/* Logo Section */}
                        <div className="mb-10">
                            <div className="flex items-center justify-center mb-6">
                                <div className="relative flex items-center">
                                    <img
                                        src={logo}
                                        alt="Pi-Schedule Logo"
                                        className="w-20 h-20 drop-shadow-sm"
                                    />
                                    <div className="absolute inset-0 w-20 h-20 bg-blue-100/30 rounded-full blur-xl"></div>
                                    <h1 className="text-4xl font-light text-slate-800 tracking-tight ml-[-8px] relative z-10">
                                        Schedule
                                    </h1>
                                </div>
                            </div>
                            <p className="text-slate-600 text-lg font-light text-center">
                                {heroSubtitle}
                            </p>
                        </div>

                        {/* User Type Selection */}
                        <div className="space-y-4 mb-8">
                            <Button
                                onClick={handleStudentClick}
                                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                            >
                                <div className="flex items-center justify-center space-x-3">
                                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                        <PiStudent className="w-5 h-5" />
                                    </div>
                                    <span>I am a Student</span>
                                </div>
                            </Button>

                            <Button
                                onClick={handleTeacherClick}
                                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white py-4 px-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
                            >
                                <div className="flex items-center justify-center space-x-3">
                                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                        <PiChalkboardTeacher className="w-5 h-5" />
                                    </div>
                                    <span>I am a Teacher</span>
                                </div>
                            </Button>
                        </div>

                        {/* Admin Section */}
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-slate-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white text-slate-500 font-medium">Administrator</span>
                            </div>
                        </div>

                        <div className="mt-6">
                            <Button
                                onClick={handleAdminLogin}
                                variant="outline"
                                className="w-full border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 py-3 px-6 font-medium transition-all duration-200"
                            >
                                <div className="flex items-center justify-center space-x-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <span>Admin Login</span>
                                </div>
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* Footer */}
                <div className="mt-8 text-center">
                    <p className="text-sm text-slate-500 font-light">
                        © 2025 Pi-Schedule. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default AdminLandingPage;
